import React, { useRef, useState } from 'react';
import { ImagePlus, RotateCcw, Upload } from 'lucide-react';
import { ILMSButton } from '../../../components/ILMSButton';
import { getApiErrorMessage } from '../../../services/api';
import { mediaUrl, uploadSiteMedia } from '../../../utils/site';

// Phone photos are often 5-10 MB; anything bigger than the page needs is
// scaled down here first (longest side `maxSide`), so the website stays fast.
async function shrink(file: File, maxSide: number): Promise<File> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    const png = file.type === 'image/png'; // keeps a logo's transparent background
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, png ? 'image/png' : 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, png ? '.png' : '.jpg'), { type: blob.type });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Upload one picture - used for the logo, each website photo and the gallery. */
export function useUpload(maxSide = 1920, uploader: (file: File) => Promise<string> = uploadSiteMedia) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = async (file: File): Promise<string | null> => {
    setBusy(true);
    setError(null);
    try {
      return await uploader(await shrink(file, maxSide));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not upload that picture.'));
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { upload, busy, error };
}

export function PhotoPicker({
  label, value, fallback, onChange, disabled, maxSide = 1920, contain = false, help, uploader,
}: {
  uploader?: (file: File) => Promise<string>; // default: the Appearance pictures endpoint
  label: string;
  value: string | null | undefined; // uploaded photo name
  fallback: string | null; // the built-in picture
  onChange: (name: string | null) => void;
  disabled?: boolean;
  maxSide?: number;
  contain?: boolean; // show the whole picture (logos)
  help?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const { upload, busy, error } = useUpload(maxSide, uploader);
  const src = value ? mediaUrl(value) : fallback;
  return (
    <div className="flex gap-3 rounded-md border border-ink-300/20 bg-white p-2.5">
      <div className="h-20 w-28 shrink-0 overflow-hidden rounded bg-surface-muted flex items-center justify-center">
        {src ? <img src={src} alt="" className={contain ? 'h-full w-full object-contain' : 'h-full w-full object-cover'} /> : <ImagePlus className="h-6 w-6 text-ink-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-label text-ink-900 truncate">{label}</p>
        <p className="text-helper-text text-ink-400">{value ? 'Your photo' : fallback ? 'Built-in picture' : 'No photo yet'}{help ? ` · ${help}` : ''}</p>
        {error && <p className="text-error-text text-critical-600">{error}</p>}
        {!disabled && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <ILMSButton size="sm" variant="outline" icon={<Upload />} loading={busy} onClick={() => input.current?.click()}>
              {value || fallback ? 'Change' : 'Upload'}
            </ILMSButton>
            {value && (
              <ILMSButton size="sm" variant="ghost" icon={<RotateCcw />} onClick={() => onChange(null)}>
                {fallback ? 'Use built-in' : 'Remove'}
              </ILMSButton>
            )}
          </div>
        )}
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            const name = await upload(file);
            if (name) onChange(name);
          }}
        />
      </div>
    </div>
  );
}
